import { Badge } from "@volca/ui/components/ui/badge";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import type { Finding } from "@/profitability/review/types";
import type { ReportRow } from "@/profitability/types";
import { formatReportPeriod } from "@/profitability/utils";
import type { RecordObject, RecordRef } from "./objects";
import { findReferenced, referenceRef, rowLinks } from "./objects";
import { RecordChip } from "./record-chip";
import { RecordKeystone } from "./record-keystone";

const SEVERITY = {
  high: { label: "High" },
  medium: { label: "Medium" },
  low: { label: "Low" },
} as const;

/**
 * A record's head, the way an order leads with "Order #1078 [Paid]
 * [Unfulfilled]" and its date and source: its name (a number reads as
 * "Job 48217"), how much its finding matters and where it stands, then one
 * line of what places it: when it happened and the records it belongs to,
 * each with its mark. Its machine stands top right.
 */
export const RecordHeader = ({
  object,
  objects,
  row,
  name,
  unit,
  finding,
  week,
  onOpenRecord,
}: {
  object: RecordObject;
  objects: RecordObject[];
  row: ReportRow;
  name: string;
  unit: string | null;
  finding: Finding | null;
  /** The period its figures are from, when not the latest report's. */
  week: { start_date: string | null; end_date: string | null } | null;
  onOpenRecord: (ref: RecordRef) => void;
}) => {
  const numbered = /^\d/.test(name);
  const title = numbered ? `${object.name} ${name}` : name;
  const dateColumn = object.dataset.columns.find(
    (column) => column.key === object.dataset.period_field,
  );
  const date = dateColumn ? row[dateColumn.key] : null;
  const links = rowLinks(object, objects, row);
  const severity = finding?.severity ? SEVERITY[finding.severity] : null;
  // A job's type says its trade more exactly than its business unit does.
  const kind = object.dataset.columns.find(
    (column) =>
      !column.references && /type|category/i.test(column.label ?? column.key),
  );
  const kindValue = kind ? row[kind.key] : null;
  return (
    <div className="flex items-start justify-between gap-4 px-5 pt-1 pb-5">
      <div className="flex min-w-0 flex-col gap-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <Text
            render={<h2 />}
            variant="heading"
            className="min-w-0 [overflow-wrap:anywhere]"
          >
            {title}
          </Text>
          {severity &&
            finding?.status !== "resolved" &&
            finding?.status !== "explained" && (
              <Badge variant="outline">{severity.label}</Badge>
            )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-subtle">
          {!numbered && <span>{object.name}</span>}
          {date && dateColumn && dayjs(String(date)).isValid() ? (
            <span>
              {dateColumn.label ?? "Date"} {dayjs(String(date)).format("MMM D")}
            </span>
          ) : (
            week?.start_date &&
            week.end_date && (
              <span>
                {formatReportPeriod({
                  start_date: week.start_date,
                  end_date: week.end_date,
                })}
              </span>
            )
          )}
          {links.map((link) => (
            <RecordChip
              key={link.key}
              object={link.target}
              name={link.name}
              variant="plain"
              trade={link.target.icon === "team" ? link.name : undefined}
              row={findReferenced(link.target, link.value)}
              onOpen={() => onOpenRecord(referenceRef(link.target, link.value))}
            />
          ))}
        </div>
      </div>
      <RecordKeystone
        icon={object.icon}
        art={object.art}
        hints={
          object.icon === "team"
            ? [name]
            : [typeof kindValue === "string" ? kindValue : null, unit]
        }
      />
    </div>
  );
};
